import { InputWithLabel } from '@adotel/components'
import {
  ApolloError,
  LazyQueryExecFunction,
  OperationVariables,
  useMutation
} from '@apollo/client'
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Hidden,
  Typography
} from '@material-ui/core'
import { Form, Formik, FormikHelpers } from 'formik'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import AdotelLogo from '../../assets/images/AdPoint1.png'
import BottomCurve from '../../assets/images/Vector.svg'
import ellipseImg from '../../assets/images/ellipse.svg'
import AdotelLogoForLogin from '../../assets/images/logo.svg'
import AltchaCaptcha, {
  AltchaCaptchaRef
} from '../../components/Captcha/AltchaCaptcha'
import SessionExpiredBanner from '../../components/SessionExpiredBanner'
import { LOGIN_MUTATION } from '../../graphql/auth'
import { getTranslate } from '../../localization'
import { Credentials, LoginResponse } from '../../types/auth'
import useStyles from './styles/userInfo.style'
import { loginValidationSchema } from './validations/loginFormValidation'

type UserInfoProps = {
  year: string
  setStep: React.Dispatch<React.SetStateAction<number>>
  setTimeLeftInSeconds: (
    value: number | ((prevState: number) => number)
  ) => void
  keepuserLoggedIn: boolean
  setkeepuserLoggedIn: React.Dispatch<React.SetStateAction<boolean>>
  getCredentials: LazyQueryExecFunction<
    {
      me: Credentials
    },
    OperationVariables
  >
}

type LoginForm = {
  email: string
  password: string
  keepMeLogedIn: boolean
}

const LOGIN_FORM_INIT_VALUES: LoginForm = {
  email: '',
  password: '',
  keepMeLogedIn: false
}

const UserInfo = ({
  year,
  setStep,
  setTimeLeftInSeconds,
  keepuserLoggedIn,
  setkeepuserLoggedIn,
  getCredentials
}: UserInfoProps) => {
  const classes = useStyles()
  const captchaRef = useRef<AltchaCaptchaRef>(null)
  const [captchaToken, setCaptchaToken] = useState('')

  // Whether the widget can actually be solved. Starts null (undetermined) so the
  // button is not disabled during the brief window before the custom element
  // upgrades — a flash of a disabled submit on every page load would be its own
  // small bug.
  const [captchaAvailable, setCaptchaAvailable] = useState<boolean | null>(null)

  // Stable identity: AltchaCaptcha takes this in a useEffect dependency list, so
  // a fresh closure each render would re-arm the availability probe every time.
  const handleAvailability = useCallback(
    (available: boolean) => setCaptchaAvailable(available),
    []
  )

  // Require a solved captcha ONLY when the widget is known to work.
  //
  // Gating on `!captchaToken` alone would be a lockout, not a safeguard: if the
  // vendored script fails to load, the widget can never emit `verified`, so the
  // token stays empty and the button would be disabled permanently with nothing
  // on screen explaining why. Leaving submission open in that case lets the
  // request reach the BFF, which is where the control actually lives — it
  // rejects an empty token before the IAM call — so the user gets a real error
  // instead of a dead form.
  const captchaBlocksSubmit = captchaAvailable === true && !captchaToken

  // Ties the submit button to the sentence explaining why it cannot be used.
  const captchaNoticeId = 'login-captcha-notice'

  const [login, { loading }] = useMutation(LOGIN_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const response: LoginResponse = data.login
      if (response.nextStep === 'UPDATE_PHONE_NUMBER') {
        setStep(2)
      } else if (response.nextStep === 'VERIFY_2FA') {
        setTimeLeftInSeconds(response.expiresIn)
        setStep(3)
      } else {
        getCredentials()
        captchaRef.current?.reset()
        setCaptchaToken('')
        toast.success('successfuly logged in')
      }
    },
    onError: (error: ApolloError) => {
      captchaRef.current?.reset()
      setCaptchaToken('')
      toast.error(error.message)
    }
  })

  const handleSubmit = async (
    values: LoginForm,
    { setSubmitting }: FormikHelpers<LoginForm>
  ) => {
    // aria-disabled does not stop a click or an Enter press -- only the attribute
    // announces the state; the gate itself has to live here.
    if (captchaBlocksSubmit) {
      setSubmitting(false)
      return
    }

    try {
      await login({
        variables: {
          input: {
            ...values,
            keepMeLogedIn: keepuserLoggedIn,
            captchaToken
          }
        }
      })
    } finally {
      if (location.pathname.includes('login')) {
        setSubmitting(false)
      }
    }
  }

  return (
    <>
      <Formik
        initialValues={LOGIN_FORM_INIT_VALUES}
        onSubmit={handleSubmit}
        validationSchema={loginValidationSchema}>
        {({
          values,
          setFieldValue,
          errors,
          touched,
          handleBlur,
          isSubmitting
        }) => (
          <Form>
            <div className={classes.root}>
              <Hidden smDown>
                <div className={classes.logoPaper}>
                  <img
                    src={AdotelLogo}
                    alt="Adotel Co."
                    className={classes.logoImg}
                  />
                  <div className={classes.copyrightContainer}>
                    <Typography>
                      ©{year} {getTranslate('Adotel')} .{' '}
                      {getTranslate('All Rights Reserved')}
                    </Typography>
                  </div>
                </div>
              </Hidden>
              <div className={classes.loginPaper}>
                <img
                  src={ellipseImg}
                  alt="adotel"
                  className={classes.ellipseIcon}
                />
                <div className={classes.formContainer}>
                  <img
                    src={AdotelLogoForLogin}
                    alt="Adotel Co."
                    className={classes.logoImgSmall}
                  />
                  <SessionExpiredBanner />
                  <div className={classes.titleSection}>
                    <Typography variant="h1" className={classes.title}>
                      {getTranslate('Central Reservation System')}
                    </Typography>
                    <Typography variant="h4" className={classes.subtitle}>
                      {getTranslate('Enter your user information')}
                    </Typography>
                  </div>
                  <Grid className={classes.inputContainer}>
                    <InputWithLabel
                      data-cy="email"
                      label={getTranslate('Email')}
                      placeholder="example@domain.com"
                      name="email"
                      inputSize="small"
                      value={values.email}
                      onChange={(event) =>
                        setFieldValue('email', event.currentTarget.value)
                      }
                      error={(touched.email && !!errors.email) || undefined}
                      helpertext={
                        touched.email
                          ? getTranslate(errors.email || '')
                          : undefined
                      }
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      fullWidth
                      className={classes.emailInput}
                    />
                  </Grid>
                  <Grid className={classes.inputContainer}>
                    <InputWithLabel
                      data-cy="password"
                      label={getTranslate('Password')}
                      placeholder={getTranslate('Enter your password')}
                      type="password"
                      name="password"
                      inputSize="small"
                      value={values.password}
                      onChange={(event) =>
                        setFieldValue('password', event.currentTarget.value)
                      }
                      error={
                        (touched.password && !!errors.password) || undefined
                      }
                      helpertext={
                        touched.password
                          ? getTranslate(errors.password || '')
                          : undefined
                      }
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      fullWidth
                      className={classes.passwordInput}
                    />
                  </Grid>
                  <Grid>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={keepuserLoggedIn}
                          onChange={(event) =>
                            setkeepuserLoggedIn(event.currentTarget.checked)
                          }
                          disabled={isSubmitting}
                          className={classes.checkbox}
                          classes={{
                            root: classes.checkboxRoot,
                            checked: classes.checkboxChecked
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          className={classes.checkboxText}>
                          {getTranslate('Keep me logged in')}
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid className={classes.captchaContainer}>
                    <AltchaCaptcha
                      ref={captchaRef}
                      onVerify={setCaptchaToken}
                      onAvailabilityChange={handleAvailability}
                    />
                    {captchaAvailable === false && (
                      <Typography
                        variant="caption"
                        data-cy="captcha-unavailable"
                        className={classes.captchaNotice}>
                        {getTranslate(
                          'Verification could not be loaded. You can still try to log in.'
                        )}
                      </Typography>
                    )}
                  </Grid>
                  <Grid className={classes.loginButtonContainer}>
                    {/*
                      `disabled` is kept for the in-flight states and deliberately NOT
                      used for the captcha state.

                      A disabled button leaves the tab order, so a keyboard or screen
                      reader user tabbing this form reached the end of it with no submit
                      control and nothing telling them why -- the sentence explaining the
                      block sits after the button and was never announced with it. The
                      control was unreachable and its reason invisible at the same time.

                      `aria-disabled` keeps the button focusable and announced as
                      unavailable, `aria-describedby` ties it to that sentence so the
                      reason is read out with it, and the submit handler refuses the
                      submission -- the gate is enforced in code, not by the attribute.

                      `isSubmitting`/`loading` stay on `disabled`: they last a moment,
                      the user has just pressed the button so focus is already on it,
                      and nobody is stranded by a control that returns on its own.
                    */}
                    <Button
                      id="login-button"
                      data-cy="submit"
                      variant="contained"
                      fullWidth
                      size="medium"
                      type="submit"
                      disabled={isSubmitting || loading}
                      aria-disabled={captchaBlocksSubmit}
                      aria-describedby={
                        captchaBlocksSubmit ? captchaNoticeId : undefined
                      }
                      className={classes.loginButton}>
                      <Typography
                        variant="body2"
                        className={classes.loginButtonText}>
                        {getTranslate('Log in')}
                      </Typography>
                      {loading && (
                        <CircularProgress
                          className={classes.circularProgress}
                          size={16}
                        />
                      )}
                    </Button>
                  </Grid>
                  {/*
                    The live region is mounted ALWAYS and only its text changes.

                    role="status" announces mutations inside a region the assistive
                    technology is already observing. A node inserted with its text
                    already in it is not a mutation of an observed region, and several
                    screen reader and browser pairs stay silent on it -- so mounting the
                    notice on demand, which is what this did, is the shape least likely
                    to be announced. The container is now present from first render and
                    the text appears inside it.

                    Empty rather than hidden: `display: none` and `aria-hidden` both
                    remove the region from the accessibility tree, which reintroduces the
                    same problem. An empty <p> occupies no visible space.
                  */}
                  <Grid>
                    <Typography
                      variant="caption"
                      id={captchaNoticeId}
                      data-cy="captcha-required"
                      role="status"
                      className={classes.captchaNotice}>
                      {captchaBlocksSubmit
                        ? getTranslate('Complete the verification to continue.')
                        : ''}
                    </Typography>
                  </Grid>
                  {/*
                    The illustration pane carries this same footer above `sm`, and both
                    are `position: fixed` at the same coordinates -- so above `sm` they
                    rendered exactly on top of each other. Invisible on screen, which is
                    why it survived; a screen reader announced the copyright twice.
                    This copy is the one for narrow viewports, where the pane is hidden.
                  */}
                  <Hidden mdUp>
                    <Grid className={classes.copyrightContainer}>
                      <Typography>
                        ©{year} {getTranslate('Adotel')} .{' '}
                        {getTranslate('All Rights Reserved')}
                      </Typography>
                    </Grid>
                  </Hidden>
                </div>
              </div>
              <img
                src={BottomCurve}
                alt="Adotel Co."
                className={classes.BottomCurve}
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default UserInfo
